<template>
  <div class="popUpWrap">
    <section class="popup light">
      <div class="topArea">
        <h4>{{ t("skillCertify-basic-info") }}</h4>
        <div class="exit" @click.stop="onClickClose"></div>
      </div>
      <div class="scrollY">
        <ul class="infoList flex">
          <li class="flex">
            <div class="tit">{{ t("common-first-name") }}</div>
            <input
              id="firstNameInput"
              v-model="modifyInfo.firstName"
              type="text"
              :class="{error: (!isValidStringFirstName && inputEditFlag.firstName) || firstNameFormatError }"
              :placeholder="t('common-placeholder-first-name')"
            >
            <p v-if="!isValidStringFirstName && inputEditFlag.firstName" class="errorMessage">
              <span></span>
              {{ t("error-first-name") }}
            </p>
            <p v-else-if="firstNameFormatError" class="errorMessage">
              <span></span>
              {{ t("error-invalid-first-name") }}
            </p>
          </li>
          <li class="flex">
            <div class="tit">{{ t("common-last-name") }}</div>
            <input
              id="lastNameInput"
              v-model="modifyInfo.lastName"
              type="text"
              :class="{error: (!isValidStringLastName && inputEditFlag.lastName) || lastNameFormatError }"
              :placeholder="t('common-placeholder-last-name')"
            >
            <p v-if="!isValidStringLastName && inputEditFlag.lastName" class="errorMessage">
              <span></span>
              {{ t("error-last-name") }}
            </p>
            <p v-else-if="lastNameFormatError" class="errorMessage">
              <span></span>
              {{ t("error-invalid-last-name") }}
            </p>
          </li>
          <li class="flex">
            <div class="tit">{{ t("profile-headline") }} <span>{{ t("common-optional", 0) }}</span></div>
            <textarea
              id="headLineInput"
              v-model="modifyInfo.headLine"
              :class="{error: !isValidStringHeadLine && inputEditFlag.headLine }"
              :placeholder="t('profile-headline-placeholder')"
            />
            <p v-if="!isValidStringHeadLine && inputEditFlag.headLine" class="errorMessage">
              <span></span>
              {{ t("profile-headline-error") }}
            </p>
          </li>
        </ul>
        <div class="btnArea">
          <button
            id="userInfoBtn"
            class="btn"
            :class="{ disabled: !isValidStringFirstName || !isValidStringLastName || !isValidStringHeadLine }"
            :disabled="!isValidStringFirstName || !isValidStringLastName || !isValidStringHeadLine"
            @click.stop="onClickSaveModifyInfo">
            {{ t("common-save") }}
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from "#imports";
import { useDialogStore } from "~/store/dialog/dialogStore";
import { useI18n } from "vue-i18n";
import { useToastStore } from "~/store/toast/toastStore";
import { ToastType } from "~/components/Common/Toast/model/ToastType";
import { ProfileUserInformationModel } from "~~/package/mapper/profile/model/ProfileUserInformationModel";
import { useProfileService } from "~~/application/composables/service/useProfileService";
import { nameValidation } from "~/utils/ValidationUtil";

const { t } = useI18n();
const { $i18n } = useNuxtApp();
const lang = computed(() =>
  $i18n.locale.value.toUpperCase()
);
const dialogStore = useDialogStore();
const toastStore = useToastStore();

const profileService = useProfileService();
const modifyInfo = ref<ProfileUserInformationModel>(new ProfileUserInformationModel());

const inputEditFlag = ref({
  firstName: false,
  lastName: false,
  headLine: false
})

const firstNameFormatError = ref(false);
const lastNameFormatError = ref(false);

onMounted(() => {
  modifyInfo.value = { ...dialogStore.options.props.modifyInfo }
});

const isValidStringFirstName = computed(() => {
  return modifyInfo.value.firstName?.length >= 1 && modifyInfo.value.firstName?.length <= 25;
})

const isValidStringLastName = computed(() => {
  return modifyInfo.value.lastName?.length >= 1 && modifyInfo.value.lastName?.length <= 25
})

const isValidStringHeadLine = computed(() => {
  return !modifyInfo.value.headLine || modifyInfo.value.headLine.length <= 200
})

const onClickSaveModifyInfo = () => {
  firstNameFormatError.value = !nameValidation(modifyInfo.value.firstName);
  lastNameFormatError.value = !nameValidation(modifyInfo.value.lastName);
  if (firstNameFormatError.value || lastNameFormatError.value) return;

  delete modifyInfo.value.aboutMeVOList;
  profileService.modifyUserProfile(modifyInfo.value).then(async response => {
    if(response.id > 0){
      await profileService.getUserMyProfile(lang.value)
      toastStore.openToast({
        toastType: ToastType.SUCCESS,
        contents: t("profile-change-success"),
      });
      onClickClose();
    }
    else {
      toastStore.openToast({
        toastType: ToastType.FAILED,
        contents: t("profile-change-fail"),
      });
    }
  }).catch(() => {
    toastStore.openToast({
      toastType: ToastType.FAILED,
      contents: t("profile-change-fail"),
    });
  })
}

const onClickClose = () => {
  dialogStore.close();
};

watch(() => modifyInfo.value.firstName, (newValue) => {
  firstNameFormatError.value = false;
  if(modifyInfo.value.firstName && newValue !== '') {
    inputEditFlag.value.firstName = true;
  }
})

watch(() => modifyInfo.value.lastName, (newValue) => {
  lastNameFormatError.value = false;
  if(modifyInfo.value.lastName && newValue !== '') {
    inputEditFlag.value.lastName = true;
  }
})

watch(() => modifyInfo.value.headLine, (newValue) => {
  if(modifyInfo.value.headLine && newValue !== '') {
    inputEditFlag.value.headLine = true;
  }
})
</script>

